import { API_URL } from '../config';

class SSEManager {
  constructor() {
    this.eventSource = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 3000; // 3秒
    this.isConnecting = false;
    this.connectionTimeout = null;
    this.heartbeatTimeout = null;
    this.lastHeartbeat = 0;
    // 添加内容更新处理器映射
    this.contentUpdateHandlers = new Map();
    this.retryCount = 0;
    this.maxRetries = 5;
    this.retryInterval = 3000;
  }

  connect() {
    if (this.eventSource || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    //console.log('正在连接到SSE服务:', `${API_URL}/api/sse`);

    try {
      this.eventSource = new EventSource(`${API_URL}/api/sse`);

      this.eventSource.onopen = () => {
        //console.log('SSE连接已建立');
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.lastHeartbeat = Date.now();
        
        // 清除连接超时
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        
        // 设置心跳检测
        this.setupHeartbeatCheck();
      };

      this.eventSource.onerror = (error) => {
        console.error('SSE连接错误:', error);
        if (this.eventSource) {
          if (this.eventSource.readyState === EventSource.CLOSED) {
            //console.log('SSE连接已关闭，尝试重新连接...');
            this.handleDisconnect();
          } else if (this.eventSource.readyState === EventSource.CONNECTING) {
            //console.log('SSE正在尝试重新连接...');
          }
        } else {
          //console.log('SSE连接对象不存在，尝试重新创建连接...');
          this.handleDisconnect();
        }
      };

      this.eventSource.addEventListener('connected', (event) => {
        //console.log('收到连接成功事件:', event.data);
        this.lastHeartbeat = Date.now();
      });

      this.eventSource.addEventListener('heartbeat', (event) => {
        //console.log('收到心跳:', event.data);
        this.lastHeartbeat = Date.now();
      });

      // 监听各种内容更新事件
      ['article', 'weekly', 'idea'].forEach(type => {
        this.eventSource.addEventListener(type, (event) => {
          // 恢复日志以便调试
          //console.log(`收到${type}事件:`, event.data);
          try {
            const data = JSON.parse(event.data);
            this.notifyListeners(type, data);
            this.lastHeartbeat = Date.now();
          } catch (err) {
            console.error(`解析${type}事件数据失败:`, err);
          }
        });
      });

      // 心跳检测
      this.eventSource.addEventListener('ping', () => {
        //console.log('收到心跳信号');
        this.lastHeartbeat = Date.now();
      });
      
      // 默认消息处理
      this.eventSource.onmessage = (event) => {
        //console.log('收到默认消息:', event.data);
        this.lastHeartbeat = Date.now();
      };
    } catch (error) {
      console.error('SSE连接创建失败:', error);
      this.isConnecting = false;
      this.eventSource = null;
      this.handleDisconnect();
    }
  }
  
  setupHeartbeatCheck() {
    // 清除现有的心跳检测
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
    }
    
    // 每30秒检查一次心跳
    this.heartbeatTimeout = setInterval(() => {
      const now = Date.now();
      // 如果超过45秒没有收到心跳，则重新连接
      if (now - this.lastHeartbeat > 45000) {
        console.warn('SSE心跳超时，重新连接');
        this.disconnect();
        this.reconnect();
      }
    }, 30000);
  }
  
  reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('达到最大重连次数，停止重连');
      return;
    }
    
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, 30000); // 最多30秒
    
    //console.log(`尝试在${delay/1000}秒后重新连接 (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    setTimeout(() => {
      if (!this.eventSource && !this.isConnecting) {
        this.connect();
      }
    }, delay);
  }

  handleDisconnect() {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      //console.log(`尝试重新连接 (${this.retryCount}/${this.maxRetries})...`);
      setTimeout(() => this.connect(), this.retryInterval);
    } else {
      //console.log('达到最大重试次数，停止重连');
    }
  }

  disconnect() {
    // 清除所有定时器
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    
    if (this.heartbeatTimeout) {
      clearInterval(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
    
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    this.isConnecting = false;
    //console.log('SSE连接已断开');
  }

  subscribe(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(callback);

    // 如果还没有连接，则建立连接
    if (!this.eventSource && !this.isConnecting) {
      this.connect();
    }

    // 返回取消订阅的函数
    return () => {
      const typeListeners = this.listeners.get(type);
      if (typeListeners) {
        typeListeners.delete(callback);
        if (typeListeners.size === 0) {
          this.listeners.delete(type);
        }
      }

      // 如果没有任何监听器了，关闭连接
      if (this.listeners.size === 0) {
        this.disconnect();
      }
    };
  }

  notifyListeners(type, data) {
    //console.log(`通知${type}监听器:`, data);
    const typeListeners = this.listeners.get(type);
    //console.log(`${type}监听器数量:`, typeListeners ? typeListeners.size : 0);
    if (typeListeners) {
      typeListeners.forEach(callback => {
        try {
          //console.log(`执行${type}监听器回调`);
          callback(data);
        } catch (err) {
          console.error(`执行${type}监听器回调时出错:`, err);
        }
      });
    }
  }

  /**
   * 创建统一的内容更新处理器
   * @param {string} contentType - 内容类型 (article, weekly, idea)
   * @param {Object} options - 配置选项
   * @param {Function} options.setItems - 设置列表项的函数
   * @param {Function} options.fetchItems - 获取列表项的函数
   * @param {Function} options.updatePagination - 更新分页的函数 (可选)
   * @param {Function} options.setAnimateItems - 设置动画项的函数 (可选)
   * @param {number} options.currentPage - 当前页码 (可选)
   * @param {number} options.pageSize - 每页大小 (可选)
   * @param {boolean} options.isHomePage - 是否为首页 (可选)
   * @returns {Function} - 取消订阅函数
   */
  createContentUpdateHandler(contentType, options) {
    const { 
      setItems, 
      fetchItems, 
      updatePagination, 
      setAnimateItems,
      currentPage = 1, 
      pageSize = 10,
      isHomePage = false
    } = options;
    
    // 创建处理函数
    const handler = async (data) => {
      // 恢复日志以便调试
      console.log(`🎯 处理${contentType}更新:`, data);
      console.log(`🎯 处理选项:`, { isHomePage, currentPage, pageSize });
      
      // 对于所有类型的事件，始终刷新数据以确保获取最新的封面图片和关联数据
      // 这样可以确保图片、标签和作者信息等关联数据正确更新
      console.log(`🔄 ${contentType}事件处理: 完全刷新数据以获取最新信息`);
      
      // 确保 fetchItems 是一个函数
      if (typeof fetchItems === 'function') {
        try {
          await fetchItems(currentPage);
          console.log(`✅ ${contentType}数据刷新成功`);
        } catch (error) {
          console.error(`❌ ${contentType}数据刷新失败:`, error);
        }
      } else {
        console.error(`❌ ${contentType}处理器中的fetchItems不是一个函数`);
      }
    };

    // 订阅内容更新
    return this.subscribe(contentType, handler);
  }
}

// 导出单例实例
export default new SSEManager();